<template>
  <div>
    <RevDialog
      background-color="low"
      :name="MODAL_NAMES.KYC.PROFILE_PASSWORD_EDIT"
      :title="i18n(translations.title)"
    >
      <template #body>
        <RevForm
          :initial-values
          :name="PASSWORD_EDIT_FORM_NAME"
          noValidate
          :validate
          @submit="handleSubmit"
        >
          <template #default="{ values, errors, hasError }">
            <RevInputPassword
              id="old-password"
              v-model="values.oldPassword"
              autocomplete="current-password"
              class="mt-16"
              :disabled="isLoading"
              :error="errors.oldPassword"
              :hide-aria-label="i18n(translations.passwordButtonHideAriaLabel)"
              :label="i18n(translations.currentPassword)"
              required
              :show-aria-label="i18n(translations.passwordButtonShowAriaLabel)"
            />

            <RevInputPassword
              id="new-password"
              v-model="values.newPassword"
              autocomplete="new-password"
              class="mt-16"
              data-test="new-password-input"
              :description="i18n(translations.passwordRules)"
              :disabled="isLoading"
              :error="errors.newPassword"
              :hide-aria-label="i18n(translations.passwordButtonHideAriaLabel)"
              :label="i18n(translations.newPassword)"
              :pattern="REGEXP_VALID_PASSWORD.source"
              required
              :show-aria-label="i18n(translations.passwordButtonShowAriaLabel)"
            />

            <RevInputPassword
              id="new-password-confirmation"
              v-model="values.newPasswordValidation"
              autocomplete="new-password"
              class="mt-16"
              data-test="new-password-confirmation-input"
              :disabled="isLoading"
              :error="errors.newPasswordValidation"
              :hide-aria-label="i18n(translations.passwordButtonHideAriaLabel)"
              :label="i18n(translations.confirmNewPassword)"
              :pattern="REGEXP_VALID_PASSWORD.source"
              required
              :show-aria-label="i18n(translations.passwordButtonShowAriaLabel)"
            />

            <div class="mt-7 flex justify-center">
              <RevButton
                id="save-button"
                class="mt-32"
                :disabled="hasError || isLoading"
                full-width="always"
                :loading="isLoading"
                type="submit"
                variant="primary"
              >
                {{ i18n(translations.save) }}
              </RevButton>
            </div>
          </template>
        </RevForm>
      </template>
    </RevDialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { HttpApiError, handleValidationErrors } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDialog } from '@ds/components/Dialog'
import {
  FORM_VALID,
  RevForm,
  type Validator,
  makeValidate,
  matchingRegExp,
  maxLength,
  required,
  setFormErrors,
} from '@ds/components/Form'
import { RevInputPassword } from '@ds/components/InputPassword'
import { closeModal } from '@ds/components/ModalBase'

import {
  MODAL_NAMES,
  REGEXP_VALID_PASSWORD,
} from '~/scopes/dashboard/constants'

import {
  PASSWORD_EDIT_FORM_NAME,
  VALIDATION_ERROR,
} from './PasswordEditModal.constants'
import translations from './PasswordEditModal.translations'

const i18n = useI18n()
const { openErrorToast, openSuccessToast } = useTheToast()
const isLoading = ref(false)

type FormValues = {
  oldPassword: string
  newPassword: string
  newPasswordValidation: string
}

const initialValues: FormValues = {
  oldPassword: '',
  newPassword: '',
  newPasswordValidation: '',
}

// custom validator
const notEqualTo = <Values extends object>(
  otherFieldName: keyof Values,
  message: string,
): Validator<Values> => {
  return (value: unknown, values: Values) => {
    const otherValue = values[otherFieldName]

    return value !== otherValue ? FORM_VALID : message
  }
}

// custom validator
const equalTo = <Values extends object>(
  otherFieldName: keyof Values,
  message: string,
): Validator<Values> => {
  return (value: unknown, values: Values) => {
    const otherValue = values[otherFieldName]

    return value === otherValue ? FORM_VALID : message
  }
}

const validate = makeValidate<typeof initialValues>({
  oldPassword: [required(i18n(translations.passwordCurrentRequired))],
  newPassword: [
    required(i18n(translations.passwordNewRequired)),
    notEqualTo('oldPassword', i18n(translations.passwordNewSameAsCurrent)),
    maxLength(64, i18n(translations.passwordTooLong)),
    matchingRegExp(REGEXP_VALID_PASSWORD, i18n(translations.passwordRules)),
  ],
  newPasswordValidation: [
    required(i18n(translations.passwordNewRequired)),
    equalTo('newPassword', i18n(translations.passwordMismatch)),
  ],
})

const $httpFetch = useHttpFetch()

const handleSubmit = async (values: FormValues) => {
  try {
    isLoading.value = true

    await $httpFetch('/auth/public/v1/users/password', {
      method: 'PATCH',
      body: {
        old_password: values.oldPassword,
        new_password: values.newPassword,
        new_password_validation: values.newPasswordValidation,
      },
    })

    openSuccessToast({ content: i18n(translations.updatePasswordSuccess) })
    closeModal(MODAL_NAMES.KYC.PROFILE_PASSWORD_EDIT)
  } catch (error) {
    const apiError = HttpApiError.fromAnyError(error as Error)

    if (apiError.type === VALIDATION_ERROR) {
      handleValidationErrors<FormValues>({
        error: apiError,
        errorsToHandleConfigs: [
          {
            kind: 'FIELD',
            target: 'old_password',
            type: '/errors/required',
            formField: 'oldPassword',
            errorMessage: i18n(translations.requiredField),
          },
          {
            kind: 'FIELD',
            target: 'old_password',
            type: '/errors/password-mismatch',
            formField: 'oldPassword',
            errorMessage: i18n(translations.passwordOldWrong),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/required',
            formField: 'newPassword',
            errorMessage: i18n(translations.requiredField),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/invalid-password',
            formField: 'newPassword',
            errorMessage: i18n(translations.invalidPassword),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-strength',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordStrength),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-too-similar',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordTooSimilar),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-too-common',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordTooCommon),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-too-long',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordTooLong),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-reuse',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordReuse),
          },
          {
            kind: 'FIELD',
            target: 'new_password_validation',
            type: '/errors/required',
            formField: 'newPasswordValidation',
            errorMessage: i18n(translations.requiredField),
          },
        ],
        setFieldErrors: (errors) => {
          setFormErrors(PASSWORD_EDIT_FORM_NAME, errors)
        },
        setToastError: (toastParams) => {
          openErrorToast(toastParams)
        },
      })

      return
    }

    openErrorToast()
  } finally {
    isLoading.value = false
  }
}
</script>
